import { Badge, Button, Spinner } from "@/ui";
import { useT } from "@/app";
import { token } from "@/design/theme";
import type { Patient, SuggestNinResult } from "../lib/types";
import { formatDate } from "../lib/format";
import { Mono } from "./Mono";

export interface DedupPanelProps {
  /** Intra-lab matches surfaced live as the operator types name/phone. */
  matches: Patient[];
  searching: boolean;
  /** Cross-lab NIN suggestion (R-11) — null until a NIN blur fires. */
  ninResult: SuggestNinResult | null;
  ninChecking: boolean;
  onUseExisting: (patient: Patient) => void;
}

/**
 * DedupPanel — live duplicate guard (§4.3). Shows likely existing dossiers (intra-lab search) and the
 * cross-lab NIN suggestion state. Never blocks: the operator may always continue with a new dossier.
 */
export function DedupPanel({
  matches,
  searching,
  ninResult,
  ninChecking,
  onUseExisting,
}: DedupPanelProps) {
  const { t } = useT();
  const hasMatches = matches.length > 0;
  const showNinNote = ninChecking || ninResult != null;

  if (!searching && !hasMatches && !showNinNote) return null;

  return (
    <div
      role="status"
      aria-live="polite"
      style={{
        marginBlockStart: token.s4,
        padding: token.s4,
        border: `1px solid ${token.line}`,
        borderRadius: token.rMd,
        background: token.surface2,
        display: "flex",
        flexDirection: "column",
        gap: token.s3,
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: token.s2 }}>
        <strong style={{ fontSize: token.fsLabel }}>{t("reception.dedup.title")}</strong>
        {searching && <Spinner label={t("reception.dedup.ninChecking")} />}
        {hasMatches && <Badge tone="warn">{t("reception.dedup.match", { n: matches.length })}</Badge>}
      </div>

      {matches.map((p) => (
        <div
          key={String(p.id)}
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: token.s3,
            paddingBlock: token.s2,
            borderBlockStart: `1px solid ${token.lineSoft}`,
          }}
        >
          <div style={{ display: "flex", flexDirection: "column", gap: token.s1 }}>
            <span>
              {p.nom} {p.prenom}
            </span>
            <span style={{ color: token.slate, fontSize: token.fsCaption }}>
              <Mono>{p.telephone}</Mono> · <Mono>{p.nin}</Mono> · {formatDate(p.date_naissance)}
            </span>
          </div>
          <Button
            size="sm"
            variant="secondary"
            onClick={() => onUseExisting(p)}
            disabled={!p.dossier}
          >
            {t("reception.dedup.useExisting")}
          </Button>
        </div>
      ))}

      {showNinNote && (
        <p style={{ margin: 0, color: token.slate, fontSize: token.fsCaption }}>
          {ninChecking
            ? t("reception.dedup.ninChecking")
            : ninResult && ninResult.available && ninResult.match
              ? t("reception.dedup.ninCentralMatch")
              : t("reception.dedup.ninCentralOff")}
        </p>
      )}
    </div>
  );
}
